import React, { useState, useEffect } from 'react';
import { useAuth } from './contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';

const AdminPanel = () => {
  const { user, token, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  // Users state
  const [users, setUsers] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', password: '', role: 'user' });
  
  // Codes Prix state
  const [codesPrix, setCodesPrix] = useState([]);
  const [newCodePrix, setNewCodePrix] = useState({ code: '', price: '' });
  const [showCodePrixForm, setShowCodePrixForm] = useState(false);
  const [searchCodePrix, setSearchCodePrix] = useState('');
  const [sortCodePrix, setSortCodePrix] = useState('code-asc'); // code-asc, code-desc, price-asc, price-desc
  
  // Media Icons state
  const [mediaIcons, setMediaIcons] = useState([]);
  const [newMediaIcon, setNewMediaIcon] = useState({ name: '', file: null });
  const [showMediaIconForm, setShowMediaIconForm] = useState(false);
  
  // Logos state
  const [logos, setLogos] = useState([]);
  const [newLogo, setNewLogo] = useState({ name: '', icon: '', color: '' });
  const [showLogoForm, setShowLogoForm] = useState(false);
  
  // Social Networks state
  const [socialNetworks, setSocialNetworks] = useState([]);
  
  // Label Logos state
  const [labelLogos, setLabelLogos] = useState([]);
  const [newLabel, setNewLabel] = useState({ name: '', file: null });
  const [showLabelForm, setShowLabelForm] = useState(false);
  
  // Genres state
  const [genres, setGenres] = useState([]);
  const [newGenre, setNewGenre] = useState({ name: '', name_en: '', color: '#FF0000' });
  const [showGenreForm, setShowGenreForm] = useState(false);
  const [editingGenre, setEditingGenre] = useState(null);
  
  // Active tab state
  const [activeTab, setActiveTab] = useState('users');
  
  // Messages
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
    } else {
      fetchUsers();
      fetchCodesPrix();
      fetchMediaIcons();
      fetchLogos();
      fetchSocialNetworks();
      fetchLabelLogos();
      fetchGenres();
    }
  }, [isAdmin, navigate]);

  // ============= FETCH FUNCTIONS =============

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des utilisateurs', err);
    }
  };

  const fetchCodesPrix = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/data/codes-prix`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCodesPrix(data.codes_prix || []);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des codes prix', err);
    }
  };

  const fetchMediaIcons = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/data/media-icons`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMediaIcons(data.icons || []);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des icônes média', err);
    }
  };

  const fetchLogos = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/data/logos-list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLogos(data.logos || []);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des logos', err);
    }
  };

  const fetchSocialNetworks = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/data/social-networks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSocialNetworks(data.social_networks || []);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des réseaux sociaux', err);
    }
  };

  const fetchLabelLogos = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/data/label-logos`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLabelLogos(data.label_logos || []);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des labels', err);
    }
  };

  const fetchGenres = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/data/genres`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setGenres(data.genres || []);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des genres', err);
    }
  };

  // Simple toast function
  const toast = {
    success: (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); },
    error: (msg) => { setError(msg); setTimeout(() => setError(''), 3000); }
  };

  // ============= USER MANAGEMENT =============

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newUser)
      });

      if (response.ok) {
        setSuccess('Utilisateur créé avec succès');
        setNewUser({ username: '', password: '', role: 'user' });
        setShowCreateForm(false);
        fetchUsers();
      } else {
        const data = await response.json();
        setError(data.detail || 'Erreur lors de la création');
      }
    } catch (err) {
      setError('Erreur lors de la création de l\'utilisateur');
    }
  };

  const handleUpdateUser = async (username, updateData) => {
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${username}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        setSuccess('Utilisateur mis à jour avec succès');
        fetchUsers();
      } else {
        setError('Erreur lors de la mise à jour');
      }
    } catch (err) {
      setError('Erreur lors de la mise à jour de l\'utilisateur');
    }
  };

  const handleDeleteUser = async (username) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur "${username}" ?`)) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${username}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setSuccess('Utilisateur supprimé avec succès');
        fetchUsers();
      } else {
        const data = await response.json();
        setError(data.detail || 'Erreur lors de la suppression');
      }
    } catch (err) {
      setError('Erreur lors de la suppression de l\'utilisateur');
    }
  };

  // ============= CODES PRIX MANAGEMENT =============

  const handleAddCodePrix = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('code', newCodePrix.code);
      formData.append('price', newCodePrix.price);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/data/codes-prix`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        setSuccess('Code prix ajouté avec succès');
        setNewCodePrix({ code: '', price: '' });
        setShowCodePrixForm(false);
        fetchCodesPrix();
      } else {
        setError('Erreur lors de l\'ajout du code prix');
      }
    } catch (err) {
      setError('Erreur lors de l\'ajout du code prix');
    }
  };

  const handleDeleteCodePrix = async (code) => {
    if (!window.confirm(`Supprimer le code "${code}" ?`)) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/data/codes-prix/${code}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setSuccess('Code prix supprimé avec succès');
        fetchCodesPrix();
      } else {
        setError('Erreur lors de la suppression');
      }
    } catch (err) {
      setError('Erreur lors de la suppression du code prix');
    }
  };

  const handleDownloadKurPrix = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/data/codes-prix/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'KUR_PRIX.txt';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setSuccess('Fichier KUR_PRIX.txt téléchargé avec succès');
      } else {
        setError('Erreur lors du téléchargement du fichier');
      }
    } catch (err) {
      setError('Erreur lors du téléchargement du fichier');
    }
  };

  const handleUploadKurPrix = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.txt')) {
      setError('Le fichier doit être un fichier .txt');
      return;
    }

    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/data/codes-prix/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        setSuccess('Fichier KUR_PRIX.txt mis à jour avec succès');
        fetchCodesPrix();
      } else {
        setError('Erreur lors du téléversement du fichier');
      }
    } catch (err) {
      setError('Erreur lors du téléversement du fichier');
    }
  };

  // ============= MEDIA ICONS MANAGEMENT =============

  const handleAddMediaIcon = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!newMediaIcon.file) {
      setError('Veuillez sélectionner un fichier');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', newMediaIcon.file);
      formData.append('name', newMediaIcon.name);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/data/media-icons`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        setSuccess('Icône média ajoutée avec succès');
        setNewMediaIcon({ name: '', file: null });
        setShowMediaIconForm(false);
        fetchMediaIcons();
      } else {
        setError('Erreur lors de l\'ajout de l\'icône');
      }
    } catch (err) {
      setError('Erreur lors de l\'ajout de l\'icône média');
    }
  };

  const handleDeleteMediaIcon = async (filename) => {
    if (!window.confirm(`Supprimer l'icône "${filename}" ?`)) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/data/media-icons/${filename}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setSuccess('Icône supprimée avec succès');
        fetchMediaIcons();
      } else {
        setError('Erreur lors de la suppression');
      }
    } catch (err) {
      setError('Erreur lors de la suppression de l\'icône');
    }
  };

  // ============= LOGOS MANAGEMENT =============

  const handleAddLogo = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!newLogo.file) {
      setError('Veuillez sélectionner un fichier');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', newLogo.file);
      formData.append('name', newLogo.name);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/data/logos-list`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        setSuccess('Logo ajouté avec succès');
        setNewLogo({ name: '', file: null });
        setShowLogoForm(false);
        fetchLogos();
      } else {
        setError('Erreur lors de l\'ajout du logo');
      }
    } catch (err) {
      setError('Erreur lors de l\'ajout du logo');
    }
  };

  const handleDeleteLogo = async (filename) => {
    if (!window.confirm(`Supprimer le logo "${filename}" ?`)) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/data/logos-list/${filename}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setSuccess('Logo supprimé avec succès');
        fetchLogos();
      } else {
        setError('Erreur lors de la suppression');
      }
    } catch (err) {
      setError('Erreur lors de la suppression du logo');
    }
  };

  // ============= FILTER AND SORT FUNCTIONS =============
  
  const getFilteredAndSortedCodesPrix = () => {
    let filtered = [...codesPrix];
    
    // Filter by search
    if (searchCodePrix) {
      filtered = filtered.filter(cp => 
        cp.code.toLowerCase().includes(searchCodePrix.toLowerCase()) ||
        cp.price.toLowerCase().includes(searchCodePrix.toLowerCase())
      );
    }
    
    // Sort
    filtered.sort((a, b) => {
      switch(sortCodePrix) {
        case 'code-asc':
          return a.code.localeCompare(b.code);
        case 'code-desc':
          return b.code.localeCompare(a.code);
        case 'price-asc':
          return parseFloat(a.price.replace(/[^0-9.,]/g, '').replace(',', '.')) - 
                 parseFloat(b.price.replace(/[^0-9.,]/g, '').replace(',', '.'));
        case 'price-desc':
          return parseFloat(b.price.replace(/[^0-9.,]/g, '').replace(',', '.')) - 
                 parseFloat(a.price.replace(/[^0-9.,]/g, '').replace(',', '.'));
        default:
          return 0;
      }
    });
    
    return filtered;
  };

  // ============= RENDER =============

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-lime-400">Administration Kuroneko</h1>
            <p className="text-gray-400">Connecté en tant que : {user?.username} (Admin)</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => navigate('/')} className="bg-gray-700 hover:bg-gray-600">
              Retour au formulaire
            </Button>
            <Button onClick={logout} variant="destructive">
              Déconnexion
            </Button>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-4 bg-red-500 text-white p-3 rounded-md">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 bg-green-500 text-white p-3 rounded-md">
            {success}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <Button
            onClick={() => setActiveTab('users')}
            className={activeTab === 'users' ? 'bg-lime-400 text-black' : 'bg-gray-700 text-white'}
          >
            👤 Utilisateurs
          </Button>
          <Button
            onClick={() => setActiveTab('labels')}
            className={activeTab === 'labels' ? 'bg-lime-400 text-black' : 'bg-gray-700 text-white'}
          >
            🏷️ Logos labels
          </Button>
          <Button
            onClick={() => setActiveTab('genres')}
            className={activeTab === 'genres' ? 'bg-lime-400 text-black' : 'bg-gray-700 text-white'}
          >
            🎵 Genres
          </Button>
          <Button
            onClick={() => setActiveTab('codes')}
            className={activeTab === 'codes' ? 'bg-lime-400 text-black' : 'bg-gray-700 text-white'}
          >
            💰 Codes prix
          </Button>
          <Button
            onClick={() => setActiveTab('media')}
            className={activeTab === 'media' ? 'bg-lime-400 text-black' : 'bg-gray-700 text-white'}
          >
            📰 Médias
          </Button>
          <Button
            onClick={() => setActiveTab('logos')}
            className={activeTab === 'logos' ? 'bg-lime-400 text-black' : 'bg-gray-700 text-white'}
          >
            📱 Réseaux et plateformes
          </Button>
        </div>

        {/* User Management */}
        {activeTab === 'users' && (
        <Card className="bg-gray-800 border-lime-400">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="text-lime-400">Gestion des utilisateurs</CardTitle>
              <Button
                onClick={() => setShowCreateForm(!showCreateForm)}
                className="bg-lime-400 text-black hover:bg-lime-500"
              >
                {showCreateForm ? 'Annuler' : '+ Nouvel utilisateur'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Create User Form */}
            {showCreateForm && (
              <form onSubmit={handleCreateUser} className="mb-6 p-4 bg-gray-700 rounded-lg space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-lime-400">Nom d'utilisateur</Label>
                    <Input
                      value={newUser.username}
                      onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                      required
                      className="bg-gray-600 text-white border-gray-500"
                    />
                  </div>
                  <div>
                    <Label className="text-lime-400">Mot de passe</Label>
                    <Input
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                      required
                      className="bg-gray-600 text-white border-gray-500"
                    />
                  </div>
                  <div>
                    <Label className="text-lime-400">Rôle</Label>
                    <Select value={newUser.role} onValueChange={(value) => setNewUser({...newUser, role: value})}>
                      <SelectTrigger className="bg-gray-600 text-white border-gray-500">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-600 text-white">
                        <SelectItem value="user">Utilisateur</SelectItem>
                        <SelectItem value="admin">Administrateur</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button type="submit" className="bg-lime-400 text-black hover:bg-lime-500">
                  Créer l'utilisateur
                </Button>
              </form>
            )}

            {/* Users List */}
            <div className="space-y-2">
              {users.map((u) => (
                <div key={u.username} className="bg-gray-700 p-4 rounded-lg flex justify-between items-center">
                  <div>
                    <p className="text-white font-bold">{u.username}</p>
                    <p className="text-gray-400 text-sm">
                      Rôle: {u.role === 'admin' ? '👑 Administrateur' : '👤 Utilisateur'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {u.username === 'Admin' ? (
                      <>
                        <Button
                          size="sm"
                          onClick={() => {
                            const newPassword = prompt('Nouveau mot de passe pour Admin:');
                            if (newPassword) {
                              handleUpdateUser(u.username, { password: newPassword });
                            }
                          }}
                          className="bg-yellow-500 hover:bg-yellow-600"
                        >
                          Changer MDP Admin
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          onClick={() => {
                            const newRole = u.role === 'admin' ? 'user' : 'admin';
                            handleUpdateUser(u.username, { role: newRole });
                          }}
                          className="bg-blue-500 hover:bg-blue-600"
                        >
                          {u.role === 'admin' ? 'Retirer admin' : 'Promouvoir admin'}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            const newPassword = prompt('Nouveau mot de passe:');
                            if (newPassword) {
                              handleUpdateUser(u.username, { password: newPassword });
                            }
                          }}
                          className="bg-yellow-500 hover:bg-yellow-600"
                        >
                          Changer MDP
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleDeleteUser(u.username)}
                          variant="destructive"
                        >
                          Supprimer
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        )}

        {/* Codes Prix Management */}
        {activeTab === 'codes' && (
          <Card className="bg-gray-800 border-lime-400">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-lime-400">Gestion des codes prix</CardTitle>
                <div className="flex gap-2">
                  <Button
                    onClick={handleDownloadKurPrix}
                    className="bg-blue-500 text-white hover:bg-blue-600"
                  >
                    ⬇️ Télécharger KUR_PRIX.txt
                  </Button>
                  <Button
                    onClick={() => document.getElementById('kur-prix-upload').click()}
                    className="bg-yellow-500 text-black hover:bg-yellow-600"
                  >
                    ⬆️ Upload KUR_PRIX.txt
                  </Button>
                  <input
                    id="kur-prix-upload"
                    type="file"
                    accept=".txt"
                    onChange={handleUploadKurPrix}
                    className="hidden"
                  />
                  <Button
                    onClick={() => setShowCodePrixForm(!showCodePrixForm)}
                    className="bg-lime-400 text-black hover:bg-lime-500"
                  >
                    {showCodePrixForm ? 'Annuler' : '+ Nouveau code prix'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {showCodePrixForm && (
                <form onSubmit={handleAddCodePrix} className="mb-6 p-4 bg-gray-700 rounded-lg space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-lime-400">Code</Label>
                      <Input
                        value={newCodePrix.code}
                        onChange={(e) => setNewCodePrix({...newCodePrix, code: e.target.value})}
                        required
                        placeholder="Ex: CD001"
                        className="bg-gray-600 text-white border-gray-500"
                      />
                    </div>
                    <div>
                      <Label className="text-lime-400">Prix</Label>
                      <Input
                        value={newCodePrix.price}
                        onChange={(e) => setNewCodePrix({...newCodePrix, price: e.target.value})}
                        required
                        placeholder="Ex: 15.99€"
                        className="bg-gray-600 text-white border-gray-500"
                      />
                    </div>
                  </div>
                  <Button type="submit" className="bg-lime-400 text-black hover:bg-lime-500">
                    Ajouter le code prix
                  </Button>
                </form>
              )}

              {/* Search and Sort Controls */}
              <div className="mb-4 p-4 bg-gray-700 rounded-lg space-y-3">
                <div className="flex gap-3">
                  <div className="flex-1">
                    <Label className="text-lime-400 mb-2 block">🔍 Rechercher</Label>
                    <Input
                      value={searchCodePrix}
                      onChange={(e) => setSearchCodePrix(e.target.value)}
                      placeholder="Rechercher par code ou prix..."
                      className="bg-gray-600 text-white border-gray-500"
                    />
                  </div>
                  <div className="w-48">
                    <Label className="text-lime-400 mb-2 block">⬆️⬇️ Trier par</Label>
                    <Select value={sortCodePrix} onValueChange={setSortCodePrix}>
                      <SelectTrigger className="bg-gray-600 text-white border-gray-500">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-600 text-white">
                        <SelectItem value="code-asc">Code (A → Z)</SelectItem>
                        <SelectItem value="code-desc">Code (Z → A)</SelectItem>
                        <SelectItem value="price-asc">Prix (↑ Croissant)</SelectItem>
                        <SelectItem value="price-desc">Prix (↓ Décroissant)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {searchCodePrix && (
                  <p className="text-gray-400 text-sm">
                    {getFilteredAndSortedCodesPrix().length} résultat(s) trouvé(s)
                  </p>
                )}
              </div>

              <div className="space-y-2">
                {codesPrix.length === 0 ? (
                  <p className="text-gray-400 text-center py-4">Aucun code prix</p>
                ) : getFilteredAndSortedCodesPrix().length === 0 ? (
                  <p className="text-gray-400 text-center py-4">Aucun résultat pour "{searchCodePrix}"</p>
                ) : (
                  getFilteredAndSortedCodesPrix().map((cp) => (
                    <div key={cp.code} className="bg-gray-700 p-4 rounded-lg flex justify-between items-center">
                      <div>
                        <p className="text-white font-bold">{cp.code}</p>
                        <p className="text-gray-400 text-sm">{cp.price}</p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleDeleteCodePrix(cp.code)}
                        variant="destructive"
                      >
                        Supprimer
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Media Icons Management */}
        {activeTab === 'media' && (
          <Card className="bg-gray-800 border-lime-400">
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="text-lime-400">Gestion des logos médias</CardTitle>
                  <p className="text-gray-400 text-sm mt-1">Utilisés pour les sections "Médias", "Et aussi" et "Marketing et réseaux" du formulaire (étape 4)</p>
                </div>
                <Button
                  onClick={() => setShowMediaIconForm(!showMediaIconForm)}
                  className="bg-lime-400 text-black hover:bg-lime-500"
                >
                  {showMediaIconForm ? 'Annuler' : '+ Nouveau média'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {showMediaIconForm && (
                <form onSubmit={handleAddMediaIcon} className="mb-6 p-4 bg-gray-700 rounded-lg space-y-4">
                  <div>
                    <Label className="text-lime-400">Nom du média</Label>
                    <Input
                      value={newMediaIcon.name}
                      onChange={(e) => setNewMediaIcon({...newMediaIcon, name: e.target.value})}
                      required
                      placeholder="Ex : Télérama, Le Monde, Le Journal du Dimanche"
                      className="bg-gray-600 text-white border-gray-500"
                    />
                  </div>
                  <div>
                    <Label className="text-lime-400">Fichier image JPG, JPEG, PNG (carré, rectangulaire ou rond)</Label>
                    <label className="flex items-center gap-2 mt-1 px-4 py-2 bg-gray-600 text-white border border-gray-500 rounded-md cursor-pointer hover:bg-gray-500 transition-colors w-fit">
                      <span className="text-sm">Cliquer pour ajouter un logo</span>
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png"
                        onChange={(e) => setNewMediaIcon({...newMediaIcon, file: e.target.files[0]})}
                        required
                        className="hidden"
                      />
                    </label>
                    {newMediaIcon.file && (
                      <p className="text-gray-400 text-xs mt-1">{newMediaIcon.file.name}</p>
                    )}
                  </div>
                  <Button type="submit" className="bg-lime-400 text-black hover:bg-lime-500">
                    Ajouter le média
                  </Button>
                </form>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {mediaIcons.length === 0 ? (
                  <p className="text-gray-400 text-center py-4 col-span-4">Aucune icône média</p>
                ) : (
                  mediaIcons.map((icon) => (
                    <div key={icon.filename} className="bg-gray-700 p-4 rounded-lg text-center">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/media-icons/${icon.filename}`}
                        alt={icon.name}
                        className="w-16 h-16 mx-auto mb-2 object-contain"
                      />
                      <p className="text-white text-sm mb-2">{icon.name}</p>
                      <div className="flex gap-1 justify-center">
                        <label className="cursor-pointer">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs border-lime-400 text-lime-400 hover:bg-lime-400 hover:text-black"
                            onClick={(e) => { e.preventDefault(); e.target.closest('label').querySelector('input')?.click(); }}
                          >
                            Modifier logo
                          </Button>
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.png"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (!file) return;
                              try {
                                const formDataUpload = new FormData();
                                formDataUpload.append('file', file);
                                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/data/media-icons/${icon.filename}`, {
                                  method: 'PUT',
                                  headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                                  body: formDataUpload
                                });
                                if (response.ok) {
                                  toast.success('Logo mis à jour');
                                  fetchMediaIcons();
                                } else {
                                  toast.error('Erreur lors de la mise à jour');
                                }
                              } catch (error) {
                                toast.error('Erreur lors de la mise à jour');
                              }
                              e.target.value = '';
                            }}
                          />
                        </label>
                        <Button
                          size="sm"
                          onClick={() => handleDeleteMediaIcon(icon.filename)}
                          variant="destructive"
                        >
                          Supprimer
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Logos Management - Réseaux et plateformes */}
        {activeTab === 'logos' && (
          <Card className="bg-gray-800 border-lime-400">
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="text-lime-400">Gestion des logos réseaux sociaux et plateformes de streaming</CardTitle>
                  <p className="text-gray-400 text-sm mt-1">Utilisés pour la section "Marketing et réseaux" du formulaire (étape 4)</p>
                </div>
                <Button
                  onClick={() => setShowLogoForm(!showLogoForm)}
                  className="bg-lime-400 text-black hover:bg-lime-500"
                >
                  {showLogoForm ? 'Annuler' : '+ Nouveau réseau'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Add new social network form */}
              {showLogoForm && (
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  if (!newLogo.name || !newLogo.file) {
                    toast.error('Veuillez remplir le nom et choisir une image');
                    return;
                  }
                  try {
                    const formDataUpload = new FormData();
                    formDataUpload.append('name', newLogo.name);
                    formDataUpload.append('file', newLogo.file);
                    
                    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/data/social-networks`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                      body: formDataUpload
                    });
                    
                    if (response.ok) {
                      const data = await response.json();
                      setSocialNetworks(data.social_networks);
                      setNewLogo({ name: '', file: null });
                      setShowLogoForm(false);
                      toast.success('Réseau social ajouté');
                    } else {
                      const error = await response.json();
                      toast.error(error.detail || 'Erreur');
                    }
                  } catch (error) {
                    toast.error('Erreur lors de l\'ajout');
                  }
                }} className="mb-6 p-4 bg-gray-700 rounded-lg space-y-4">
                  <div>
                    <Label className="text-lime-400">Nom du réseau / plateforme</Label>
                    <Input
                      value={newLogo.name}
                      onChange={(e) => setNewLogo({...newLogo, name: e.target.value})}
                      required
                      placeholder="Ex : Facebook, Instagram, Spotify"
                      className="bg-gray-600 text-white border-gray-500"
                    />
                  </div>
                  <div>
                    <Label className="text-lime-400">Fichier image JPG, JPEG, PNG (carré, rectangulaire ou rond)</Label>
                    <label className="flex items-center gap-2 mt-1 px-4 py-2 bg-gray-600 text-white border border-gray-500 rounded-md cursor-pointer hover:bg-gray-500 transition-colors w-fit">
                      <span className="text-sm">Cliquer pour ajouter un logo</span>
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png"
                        onChange={(e) => setNewLogo({...newLogo, file: e.target.files[0]})}
                        required
                        className="hidden"
                      />
                    </label>
                    {newLogo.file && (
                      <p className="text-gray-400 text-xs mt-1">{newLogo.file.name}</p>
                    )}
                  </div>
                  <Button type="submit" className="bg-lime-400 text-black hover:bg-lime-500">
                    Ajouter le réseau
                  </Button>
                </form>
              )}
              
              <p className="text-lime-400 font-medium mb-4">Réseaux sociaux disponibles ({socialNetworks.length})</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {socialNetworks.map((social) => (
                  <div key={social.name} className="bg-gray-700 p-4 rounded-lg text-center">
                    {social.filename ? (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/social-icons/${social.filename}`}
                        alt={social.name}
                        className="w-16 h-16 mx-auto mb-2 object-contain"
                      />
                    ) : (
                      <div className="w-16 h-16 mx-auto mb-2 bg-gray-600 rounded-lg flex items-center justify-center text-gray-400 text-2xl">?</div>
                    )}
                    <p className="text-white text-sm mb-2">{social.name}</p>
                    <div className="flex gap-1 justify-center">
                      <label className="cursor-pointer">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs border-lime-400 text-lime-400 hover:bg-lime-400 hover:text-black"
                          onClick={(e) => { e.preventDefault(); e.target.closest('label').querySelector('input')?.click(); }}
                        >
                          Modifier logo
                        </Button>
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            try {
                              const formDataUpload = new FormData();
                              formDataUpload.append('file', file);
                              const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/data/social-networks/${encodeURIComponent(social.name)}`, {
                                method: 'PUT',
                                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                                body: formDataUpload
                              });
                              if (response.ok) {
                                const data = await response.json();
                                setSocialNetworks(data.social_networks);
                                toast.success('Logo mis à jour');
                              } else {
                                toast.error('Erreur lors de la mise à jour');
                              }
                            } catch (error) {
                              toast.error('Erreur lors de la mise à jour');
                            }
                            e.target.value = '';
                          }}
                        />
                      </label>
                      <Button
                        size="sm"
                        onClick={async () => {
                          if (confirm(`Supprimer "${social.name}" ?`)) {
                            try {
                              const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/data/social-networks/${encodeURIComponent(social.name)}`, {
                                method: 'DELETE',
                                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                              });
                              if (response.ok) {
                                const data = await response.json();
                                setSocialNetworks(data.social_networks);
                                toast.success('Réseau supprimé');
                              }
                            } catch (error) {
                              toast.error('Erreur lors de la suppression');
                            }
                          }
                        }}
                        variant="destructive"
                      >
                        Supprimer
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Labels Management */}
        {activeTab === 'labels' && (
          <Card className="bg-gray-800 border-lime-400">
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="text-lime-400">Gestion des logos labels</CardTitle>
                  <p className="text-gray-400 text-sm mt-1">Logos des labels disponibles dans le formulaire (étape 1)</p>
                </div>
                <Button
                  onClick={() => setShowLabelForm(!showLabelForm)}
                  className="bg-lime-400 text-black hover:bg-lime-500"
                >
                  {showLabelForm ? 'Annuler' : '+ Nouveau label'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {showLabelForm && (
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  if (!newLabel.name || !newLabel.file) {
                    toast.error('Veuillez remplir le nom et choisir une image');
                    return;
                  }
                  try {
                    const formDataUpload = new FormData();
                    formDataUpload.append('name', newLabel.name);
                    formDataUpload.append('file', newLabel.file);
                    
                    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/data/label-logos`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                      body: formDataUpload
                    });
                    
                    if (response.ok) {
                      const data = await response.json();
                      setLabelLogos(data.label_logos);
                      setNewLabel({ name: '', file: null });
                      setShowLabelForm(false);
                      toast.success('Label ajouté');
                    } else {
                      const error = await response.json();
                      toast.error(error.detail || 'Erreur');
                    }
                  } catch (error) {
                    toast.error("Erreur lors de l'ajout");
                  }
                }} className="mb-6 p-4 bg-gray-700 rounded-lg space-y-4">
                  <div>
                    <Label className="text-lime-400">Nom du label</Label>
                    <Input
                      value={newLabel.name}
                      onChange={(e) => setNewLabel({...newLabel, name: e.target.value})}
                      required
                      placeholder="Ex : 29Music, Sony Music, Universal"
                      className="bg-gray-600 text-white border-gray-500"
                    />
                  </div>
                  <div>
                    <Label className="text-lime-400">Fichier image JPG, JPEG, PNG (carré, rectangulaire ou rond)</Label>
                    <label className="flex items-center gap-2 mt-1 px-4 py-2 bg-gray-600 text-white border border-gray-500 rounded-md cursor-pointer hover:bg-gray-500 transition-colors w-fit">
                      <span className="text-sm">Cliquer pour ajouter un logo</span>
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png"
                        onChange={(e) => setNewLabel({...newLabel, file: e.target.files[0]})}
                        required
                        className="hidden"
                      />
                    </label>
                    {newLabel.file && (
                      <p className="text-gray-400 text-xs mt-1">{newLabel.file.name}</p>
                    )}
                  </div>
                  <Button type="submit" className="bg-lime-400 text-black hover:bg-lime-500">
                    Ajouter le label
                  </Button>
                </form>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {labelLogos.length === 0 ? (
                  <p className="text-gray-400 text-center py-4 col-span-4">Aucun label</p>
                ) : (
                  labelLogos.map((label) => (
                    <div key={label.name} className="bg-gray-700 p-4 rounded-lg text-center">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/label-logos/${label.filename}`}
                        alt={label.name}
                        className="w-16 h-16 mx-auto mb-2 object-contain"
                      />
                      <p className="text-white text-sm mb-2">{label.name}</p>
                      <div className="flex gap-1 justify-center">
                        <label className="cursor-pointer">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs border-lime-400 text-lime-400 hover:bg-lime-400 hover:text-black"
                            onClick={(e) => { e.preventDefault(); e.target.closest('label').querySelector('input')?.click(); }}
                          >
                            Modifier logo
                          </Button>
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.png"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (!file) return;
                              try {
                                const formDataUpload = new FormData();
                                formDataUpload.append('file', file);
                                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/data/label-logos/${encodeURIComponent(label.name)}`, {
                                  method: 'PUT',
                                  headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                                  body: formDataUpload
                                });
                                if (response.ok) {
                                  const data = await response.json();
                                  setLabelLogos(data.label_logos);
                                  toast.success('Logo mis à jour');
                                } else {
                                  toast.error('Erreur lors de la mise à jour');
                                }
                              } catch (error) {
                                toast.error('Erreur lors de la mise à jour');
                              }
                              e.target.value = '';
                            }}
                          />
                        </label>
                        <Button
                          size="sm"
                          onClick={async () => {
                            if (confirm(`Supprimer "${label.name}" ?`)) {
                              try {
                                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/data/label-logos/${encodeURIComponent(label.name)}`, {
                                  method: 'DELETE',
                                  headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                });
                                if (response.ok) {
                                  const data = await response.json();
                                  setLabelLogos(data.label_logos);
                                  toast.success('Label supprimé');
                                }
                              } catch (error) {
                                toast.error('Erreur lors de la suppression');
                              }
                            }
                          }}
                          variant="destructive"
                        >
                          Supprimer
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Genres Management */}
        {activeTab === 'genres' && (
          <Card className="bg-gray-800 border-lime-400">
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="text-lime-400">Gestion des genres</CardTitle>
                  <p className="text-gray-400 text-sm mt-1">Genres FR / EN et couleurs disponibles dans le formulaire (étape 1)</p>
                </div>
                <Button
                  onClick={() => setShowGenreForm(!showGenreForm)}
                  className="bg-lime-400 text-black hover:bg-lime-500"
                >
                  {showGenreForm ? 'Annuler' : '+ Nouveau genre'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {showGenreForm && (
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  if (!newGenre.name) {
                    toast.error('Veuillez remplir le nom du genre');
                    return;
                  }
                  try {
                    const formData = new FormData();
                    formData.append('name', newGenre.name);
                    formData.append('name_en', newGenre.name_en || newGenre.name);
                    formData.append('color', newGenre.color);
                    
                    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/data/genres`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                      body: formData
                    });
                    
                    if (response.ok) {
                      const data = await response.json();
                      setGenres(data.genres);
                      setNewGenre({ name: '', name_en: '', color: '#FF0000' });
                      setShowGenreForm(false);
                      toast.success('Genre ajouté');
                    } else {
                      const err = await response.json();
                      toast.error(err.detail || 'Erreur');
                    }
                  } catch (error) {
                    toast.error("Erreur lors de l'ajout");
                  }
                }} className="mb-6 p-4 bg-gray-700 rounded-lg space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-lime-400">Nom du genre (FR)</Label>
                      <Input
                        data-testid="genre-name-fr-input"
                        value={newGenre.name}
                        onChange={(e) => setNewGenre({...newGenre, name: e.target.value})}
                        required
                        placeholder="Ex : ROCK, POP, HIP-HOP"
                        className="bg-gray-600 text-white border-gray-500"
                      />
                    </div>
                    <div>
                      <Label className="text-lime-400">Nom du genre (EN) — modèle Export</Label>
                      <Input
                        data-testid="genre-name-en-input"
                        value={newGenre.name_en}
                        onChange={(e) => setNewGenre({...newGenre, name_en: e.target.value})}
                        placeholder="Ex : CLASSICAL, SOUNDTRACK, KIDS"
                        className="bg-gray-600 text-white border-gray-500"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-lime-400">Code couleur</Label>
                    <div className="flex gap-2 items-center">
                      <Input
                        value={newGenre.color}
                        onChange={(e) => setNewGenre({...newGenre, color: e.target.value})}
                        placeholder="#FF0000"
                        className="bg-gray-600 text-white border-gray-500 flex-1"
                      />
                      <input 
                        type="color" 
                        value={newGenre.color}
                        onChange={(e) => setNewGenre({...newGenre, color: e.target.value})}
                        className="w-12 h-10 rounded cursor-pointer border-0"
                      />
                    </div>
                  </div>
                  <Button type="submit" className="bg-lime-400 text-black hover:bg-lime-500">
                    Ajouter le genre
                  </Button>
                </form>
              )}

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {genres.length === 0 ? (
                  <p className="text-gray-400 text-center py-4 col-span-4">Aucun genre</p>
                ) : (
                  genres.map((genre) => (
                    <div key={genre.name} className="bg-gray-700 p-4 rounded-lg">
                      {editingGenre === genre.name ? (
                        <form onSubmit={async (e) => {
                          e.preventDefault();
                          const newName = e.target.elements.genreName.value;
                          const newNameEn = e.target.elements.genreNameEn.value;
                          const newColor = e.target.elements.genreColor.value;
                          try {
                            const formData = new FormData();
                            if (newName !== genre.name) formData.append('new_name', newName);
                            formData.append('name_en', newNameEn);
                            formData.append('color', newColor);
                            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/data/genres/${encodeURIComponent(genre.name)}`, {
                              method: 'PUT',
                              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                              body: formData
                            });
                            if (response.ok) {
                              const data = await response.json();
                              setGenres(data.genres);
                              setEditingGenre(null);
                              toast.success('Genre mis à jour');
                            } else {
                              const err = await response.json().catch(() => ({}));
                              toast.error(err.detail || 'Erreur lors de la mise à jour');
                            }
                          } catch (error) {
                            toast.error('Erreur lors de la mise à jour');
                          }
                        }} className="space-y-2">
                          <Input name="genreName" data-testid="genre-edit-name-fr" defaultValue={genre.name} placeholder="Nom FR" className="bg-gray-600 text-white border-gray-500 text-sm" />
                          <Input name="genreNameEn" data-testid="genre-edit-name-en" defaultValue={genre.name_en || genre.name} placeholder="Nom EN" className="bg-gray-600 text-white border-gray-500 text-sm" />
                          <div className="flex gap-2">
                            <Input name="genreColor" defaultValue={genre.color} className="bg-gray-600 text-white border-gray-500 text-sm flex-1" />
                            <input type="color" name="genreColorPicker" defaultValue={genre.color} onChange={(e) => { e.target.closest('form').elements.genreColor.value = e.target.value; }} className="w-10 h-9 rounded cursor-pointer border-0" />
                          </div>
                          <div className="flex gap-1">
                            <Button type="submit" size="sm" className="bg-lime-400 text-black text-xs">Sauver</Button>
                            <Button type="button" size="sm" variant="ghost" onClick={() => setEditingGenre(null)} className="text-gray-400 text-xs">Annuler</Button>
                          </div>
                        </form>
                      ) : (
                        <>
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-8 h-8 rounded-full flex-shrink-0" style={{ backgroundColor: genre.color }}></div>
                            <div className="min-w-0">
                              <p className="text-white text-sm font-medium truncate">{genre.name}</p>
                              <p className="text-blue-300 text-xs truncate" data-testid={`genre-en-${genre.name}`}>EN : {genre.name_en || genre.name}</p>
                              <p className="text-gray-400 text-xs">{genre.color}</p>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs border-lime-400 text-lime-400 hover:bg-lime-400 hover:text-black"
                              onClick={() => setEditingGenre(genre.name)}
                            >
                              Modifier
                            </Button>
                            <Button
                              size="sm"
                              onClick={async () => {
                                if (confirm(`Supprimer "${genre.name}" ?`)) {
                                  try {
                                    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/data/genres/${encodeURIComponent(genre.name)}`, {
                                      method: 'DELETE',
                                      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                    });
                                    if (response.ok) {
                                      const data = await response.json();
                                      setGenres(data.genres);
                                      toast.success('Genre supprimé');
                                    } else {
                                      const err = await response.json().catch(() => ({}));
                                      toast.error(err.detail || 'Erreur lors de la suppression');
                                    }
                                  } catch (error) {
                                    toast.error('Erreur lors de la suppression');
                                  }
                                }
                              }}
                              variant="destructive"
                            >
                              Supprimer
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
